import React, {useEffect, useRef, useState} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLoading, usePersistedTransactionId } from '../../context';
import {
  HeroLogo,
  Footer,
  Button,
  Label,
  TextInput,
  FormItemContainer,
  FormContainer, SingleLineFormItemWrapper,
} from '../../components';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import AliPayPlusPaymentImage from '../../resource/figma-images/alipay-plus-payment-image.png';
import ContinueToPayImage from '../../resource/figma-images/continue-to-pay-image.svg';
import CreditCardPaymentImage from '../../resource/figma-images/credit-card-payment-image.svg';
import CreditCardPaymentImageFour from '../../resource/creditCardPaymentImage-four.png';
import { TillPaymentInlineForm } from './TillPaymentInlineForm';
import {Alert, Dialog, DialogActions, DialogContent, DialogTitle, Stack, styled, Typography, Button as RawMuiButton} from '@mui/material';
import ReCAPTCHA from "react-google-recaptcha";
import {
  getDefaultPaymentMethod,
  getPageHeroLogo, getPageTitle,
  getThemeColor,
  shouldHideAlipayPlus,
  shouldVerifyRecaptcha
} from "../../customHostConfigOnClient";
import {
  PaymentContent,
  PaymentDetails, PaymentHiddenWrapper,
  PaymentOption,
  PaymentOptionImage,
  PaymentOptionList,
  PaymentOptionText,
  SurchargeAmountWrapper
} from "../../components/PaymentMethod";
import {useRecaptchaReady, verifyRecaptcha} from "../../utils/useRecaptchaReady";
import {PrivacyPolicy} from "../../components/PrivacyPolicy";


const InfoMessage = styled('div')`
  color: darkgray;
  text-align: center;
`;

const PaymentFormPageTitle = styled('div')`
  font-size: 15px;
  text-align: center;
  font-weight: 500;
  color: #747F94;
`;


const INITIAL_PAYMENT_METHOD = 'initial payment method';

export const AUBridgePaymentForm = () => {
  const [billAmount, setBillAmount] = useState('');
  const [billReference, setBillReference] = useState('');
  const [billAmountDisabled, setBillAmountDisabled] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [error, setError] = useState('');
  const [paymentResult, setPaymentResult] = useState('');
  const [billAmountError, setBillAmountError] = useState(false);
  const [billReferenceError, setBillReferenceError] = useState(false);
  const [ReceiptAccountName, setReceiptAccountName] = useState('');
  const [ReceiptAccountNameError, setReceiptAccountNameError] = useState(false);
  const [ReceiptReference, setReceiptReference] = useState('');
  const [ReceiptReferenceError, setReceiptReferenceError] = useState(false);
  const [ReceiptBSB, setReceiptBSB] = useState('');
  const [ReceiptBSBError, setReceiptBSBError] = useState(false);
  const [ReceiptAccountNo, setReceiptAccountNo] = useState('');
  const [ReceiptAccountNoError, setReceiptAccountNoError] = useState(false);
  const [ReceiptSwiftCode, setReceiptSwiftCode] = useState('');
  const [ReceiptSwiftCodeError, setReceiptSwiftCodeError] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState('AUD');
  const [USDCurrencyExchangeRate, setUSDCurrencyExchangeRate] = useState(0);
  const tillPaymentInlineFormRef = useRef(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const recaptchaRef = useRef(null);
  const recaptchaReady = useRecaptchaReady();
  const { showLoading, hideLoading } = useLoading();
  const persistedTransactionId = usePersistedTransactionId();

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(INITIAL_PAYMENT_METHOD);

  // Hide /aubridge-flow page for other domains.
  if (window.location.hostname !== 'aub.oligo.com.au' && window.location.hostname !== 'localhost') {
    window.location = '/' + window.location.search;
  }

  // Fetch the WeChat exchange rate when the component is mounted
  useEffect(() => {
    const fetchExchangeRate = async () => {
      try {
        const response = await fetch('/api/air-wallex/current-exchange-rate');
        const data = await response.json();

        if (data.return_code === 'SUCCESS') {
          setUSDCurrencyExchangeRate(parseFloat(data.exchange_rate));
        } else {
          console.error('Failed to fetch exchange rate');
        }
      } catch (err) {
        console.error('Error fetching exchange rate', err);
      }
    };

    fetchExchangeRate();
  }, []);

  useEffect(() => {
    if (paymentResult !== '') {
      setError('');
      setSelectedPaymentMethod('initial payment method');
    }
  }, [paymentResult]);

  const handlePaymentMethodChange =
    (paymentMethod) => () => {
      setSelectedPaymentMethod(prev => (prev !== paymentMethod ? paymentMethod : prev));
    };

  const envParam = new URLSearchParams(useLocation().search).get('env');

  const handleBillAmountChange = (value) => {
    if (isValidFloat(value)) {
      setBillAmount(value);
      setBillAmountError(false);
    }
  };

  const handleBillReferenceChange = (value) => {
    setBillReference(value);
    setBillReferenceError(false);
  };

  const handleReceiptAccountNameChange = (value) => {
    setReceiptAccountName(value);
    setReceiptAccountNameError(false);
  };
  const handleReceiptReferenceChange = (value) => {
    setReceiptReference(value);
    setReceiptReferenceError(false);
  };
  const handleReceiptBSBChange = (value) => {
    setReceiptBSB(value);
    setReceiptBSBError(false);
  };
  const handleReceiptAccountNoChange = (value) => {
    setReceiptAccountNo(value);
    setReceiptAccountNoError(false);
  };
  const handleReceiptSwiftCodeChange = (value) => {
    setReceiptSwiftCode(value);
    setReceiptSwiftCodeError(false);
  };

  const isValidFloat = (value) => {
    return /^-?\d*\.?\d*$/.test(value);
  };

  const getSelectedCurrencyExchangeRate = () => {
    switch (selectedCurrency) {
      case 'USD':
        return parseFloat((USDCurrencyExchangeRate / 1.01).toFixed(6)); // Adding a 1% buffer to account for exchange rate fluctuations
      case 'AUD':
        return 1.00;
      default:
        return 0;
    }
  }

  const calculateAUDAmount = () => {
    const amount = parseFloat(billAmount);
    if (isNaN(amount)) return '';
    return (amount / getSelectedCurrencyExchangeRate()).toFixed(2);
  };

  const displayAUDPaymentAmount = () => {
    const paymentAmount = calculateAUDAmount();
    if (paymentAmount === '') return '\u00A0';
    return `AUD ${paymentAmount}`;
  };

  const createPaymentOrderAndNavigateForAlipay = async (data) => {
    window.location.href = data.redirect_url;
  };

  const getPaymentEndpoint = (selectedPayment) => {
    switch (selectedPayment) {
      case 'alipay':
        return '/api/alipay/create-payment';
      default:
        return '';
    }
  }

  const getPaymentMethodName = (selectPayment) => {
    switch (selectPayment) {
      case 'tillPaymentJs':
        return 'Credit Card';
      case 'alipay':
        return 'Alipay';
      default:
        return '';
    }
  }

  const getCurrentAUBSurchargeRate = () => {
    const defaultAUBSurchargeRate = 0.02;
    return defaultAUBSurchargeRate;
  }

  function getFinalBillAmount() {
    return (calculateAUDAmount() * (1 + getCurrentAUBSurchargeRate())).toFixed(2);
  }

  const createPaymentOrderAndNavigate = async () => {
    if (shouldVerifyRecaptcha() && recaptchaReady && recaptchaRef.current) {
      // Validate ReCAPTCHA
      await verifyRecaptcha(recaptchaRef, setError);
    }

    try {
      const extraPaymentReceiptDetails =  {
        receiptDetails: {
          ReceiptAccountName,
          ReceiptReference,
          ReceiptBSB,
          ReceiptAccountNo,
          ReceiptSwiftCode,
        }
      }
      if (selectedPaymentMethod === 'tillPaymentJs') {
        await tillPaymentInlineFormRef.current.paymentSubmit(setPaymentResult, '/api/alipay-credit-card/create-payment-with-extra-receipt-details', extraPaymentReceiptDetails);
      } else {
        showLoading();

        const createPaymentEndpoint = getPaymentEndpoint(selectedPaymentMethod);
        const response = await fetch(`${createPaymentEndpoint}?env=${envParam}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            billAmount: getFinalBillAmount(),
            billReference,
            userEmail,
            persistedTransactionId,
            ...extraPaymentReceiptDetails,
          }),
        });
        const data = await response.json();
        if (data.return_code === 'SUCCESS') {
          if (selectedPaymentMethod === 'alipay' || selectedPaymentMethod === 'alipay-dana') {
            await createPaymentOrderAndNavigateForAlipay(data);
          }
        } else {
          hideLoading('Payment creation failed.', 'error');
        }
      }
    } catch (err) {
      hideLoading();
      setError('Create payment order failed, please try again.');
    }
  };

  const handlePayBill = async () => {
    if (!billAmount) {
      setBillAmountError(true);
      return;
    }

    if (!billReference) {
      setBillReferenceError(true);
      return;
    }

    if (!ReceiptAccountName) {
      setReceiptAccountNameError(true);
      return;
    }
    if (!ReceiptReference) {
      setReceiptReferenceError(true);
      return;
    }
    if (!ReceiptAccountNo) {
      setReceiptAccountNoError(true);
      return;
    }

    setBillAmountDisabled(true);

    if (selectedPaymentMethod === 'initial payment method') {
      setError('Please select a payment method.');
      return;
    }

    setConfirmDialogOpen(true);
  };

  return (
    <FormContainer>
      <HeroLogo logo={getPageHeroLogo()} redirectPath='/aubridge-flow'/>
      <Stack gap="20px" alignItems="center">
        <PaymentFormPageTitle>{ getPageTitle() }</PaymentFormPageTitle>
        {error && <Alert severity="error">{error}</Alert>}
        <Stack gap="20px" width="100%">
          <SingleLineFormItemWrapper>
            <FormItemContainer marginRight={'10px'} flexBasis={'50%'} flexGrow={2}>
              <Label isRequired>Invoice Amount</Label>
              <TextInput
                placeholder="$"
                value={billAmount || ''}
                onChange={handleBillAmountChange}
                error={billAmountError}
                disabled={billAmountDisabled}
              />
            </FormItemContainer>
            <FormItemContainer paddingRight={'10px'} flexBasis={'30%'} flexGrow={0}>
              <Label>Currency</Label>
              <Select style={{borderRadius: '10px', height: '39px', width: '100%', fontSize:'15px' }} id="currency-selector" value={selectedCurrency} onChange={(e) => setSelectedCurrency(e.target.value)}
              >
                <MenuItem value="AUD">AUD</MenuItem>
                <MenuItem value="USD">USD</MenuItem>
              </Select>
            </FormItemContainer>
            <FormItemContainer flexBasis={'20%'} flexGrow={2}>
              <Label>FX Rate</Label>
              <TextInput style={{width: '100%'}} disabled value={getSelectedCurrencyExchangeRate() || '\u00A0'} />
            </FormItemContainer>
          </SingleLineFormItemWrapper>

          <SingleLineFormItemWrapper>
            <FormItemContainer marginRight={'10px'}>
              <Label>Payment Amount AUD</Label>
              <TextInput disabled value={displayAUDPaymentAmount()} />
            </FormItemContainer>
            <FormItemContainer>
              <Label isRequired>Your reference</Label>
              <TextInput
                placeholder='Invoice Reference Number'
                value={billReference || ''}
                onChange={handleBillReferenceChange}
                error={billReferenceError}
                disabled={billAmountDisabled}
              />
            </FormItemContainer>
          </SingleLineFormItemWrapper>

          <div>
            <Label isRequired>Recipient Details</Label>

            <SingleLineFormItemWrapper>
              <FormItemContainer marginRight={'10px'}>
                <TextInput
                  placeholder='Account Name'
                  value={ReceiptAccountName || ''}
                  onChange={handleReceiptAccountNameChange}
                  error={ReceiptAccountNameError}
                  disabled={billAmountDisabled}
                />
              </FormItemContainer>
              <FormItemContainer>
                <TextInput
                  placeholder='Reference'
                  value={ReceiptReference || ''}
                  onChange={handleReceiptReferenceChange}
                  error={ReceiptReferenceError}
                  disabled={billAmountDisabled}
                />
              </FormItemContainer>
            </SingleLineFormItemWrapper>
            <div style={{height: '10px'}}>&nbsp;</div>
            <SingleLineFormItemWrapper>
              <FormItemContainer marginRight={'10px'}>
                <TextInput
                  placeholder='BSB – (6 digits)'
                  value={ReceiptBSB || ''}
                  onChange={handleReceiptBSBChange}
                  error={ReceiptBSBError}
                  disabled={billAmountDisabled}
                />
              </FormItemContainer>
              <FormItemContainer>
                <TextInput
                  placeholder='Account Number'
                  value={ReceiptAccountNo || ''}
                  onChange={handleReceiptAccountNoChange}
                  error={ReceiptAccountNoError}
                  disabled={billAmountDisabled}
                />
              </FormItemContainer>
            </SingleLineFormItemWrapper>
            <div style={{height: '10px'}}>&nbsp;</div>
            <SingleLineFormItemWrapper>
              <FormItemContainer flexBasis={'49.2%'} flexGrow={'0'}>
                <TextInput
                  placeholder='SWIFT Code'
                  value={ReceiptSwiftCode || ''}
                  onChange={handleReceiptSwiftCodeChange}
                  error={ReceiptSwiftCodeError}
                  disabled={billAmountDisabled}
                />
              </FormItemContainer>
            </SingleLineFormItemWrapper>
          </div>

          <FormItemContainer>
            <Label>Receipt Email</Label>
            <TextInput
              placeholder="Enter your email if you would like a receipt of payment confirmation"
              value={userEmail || ''}
              onChange={setUserEmail}
            />
          </FormItemContainer>

          <div>
            <Label>Payment Method</Label>
            { !getDefaultPaymentMethod() && <div style={{color: 'rgba(84, 84, 84, 0.56)', marginBottom: '15px', fontSize: '13px'}}>Please select a payment method.</div> }

            <PaymentOptionList>
              <PaymentOption
                onClick={handlePaymentMethodChange('tillPaymentJs')}
                selected={selectedPaymentMethod === 'tillPaymentJs'}
                borderColor={getThemeColor()}
              >
                <PaymentOptionImage src={CreditCardPaymentImage} />
                <PaymentOptionText>Card</PaymentOptionText>
              </PaymentOption>
              { !shouldHideAlipayPlus() &&
                <PaymentOption
                  onClick={handlePaymentMethodChange('alipay')}
                  selected={selectedPaymentMethod === 'alipay'}
                  borderColor={getThemeColor()}
                >
                  <PaymentOptionImage src={AliPayPlusPaymentImage} />
                  <PaymentOptionText>Alipay</PaymentOptionText>
                </PaymentOption>
              }
            </PaymentOptionList>

            { selectedPaymentMethod !== INITIAL_PAYMENT_METHOD && getCurrentAUBSurchargeRate() &&
              <SurchargeAmountWrapper>{getPaymentMethodName(selectedPaymentMethod)} Surcharge of {(getCurrentAUBSurchargeRate() * 100).toFixed(1)}%</SurchargeAmountWrapper>
            }

            <PaymentDetails>
              <PaymentHiddenWrapper show={selectedPaymentMethod === 'tillPaymentJs'}>
                <TillPaymentInlineForm ref={tillPaymentInlineFormRef}
                                       billAmount={getFinalBillAmount()}
                                       billReference={billReference}
                                       userEmail={userEmail}
                                       initLoadingFinishCallback={() => getDefaultPaymentMethod() && setSelectedPaymentMethod(getDefaultPaymentMethod())}
                                       creditCardImage={CreditCardPaymentImageFour}
                />
              </PaymentHiddenWrapper>
              <PaymentHiddenWrapper show={selectedPaymentMethod === 'alipay'}>
                <PaymentContent>
                  <img src={ContinueToPayImage} />
                  <div>Select ‘Continue to pay’ and you will be redirected to Alipay to complete payment.</div>
                </PaymentContent>
              </PaymentHiddenWrapper>
            </PaymentDetails>

            { paymentResult && <InfoMessage>{paymentResult}</InfoMessage> }

            { shouldVerifyRecaptcha() && <ReCAPTCHA
              ref={recaptchaRef}
              sitekey="6LcWNAUrAAAAAHX2haltRqZqMAMZ0qB29zv16EB8"
              size="invisible"
            /> }

            <Button $themeColor={getThemeColor()} onClick={handlePayBill} block>
              Continue to pay
            </Button>
          </div>

        </Stack>
      </Stack>

      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>Confirm Payment</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to continue to pay?
          </Typography>
        </DialogContent>
        <DialogActions>
          <RawMuiButton variant="outlined" color="error" size="small"
                        onClick={() => setConfirmDialogOpen(false)} >
            No
          </RawMuiButton>
          <RawMuiButton variant="contained" color="error" size="small"
                        onClick={async () => {
                          setConfirmDialogOpen(false);
                          await createPaymentOrderAndNavigate();
                        }}
          >
            Yes
          </RawMuiButton>
        </DialogActions>
      </Dialog>

      <PrivacyPolicy />
      <Footer />
    </FormContainer>
  );
};
